/* eslint-disable consistent-return */
// Offline queue for PostHog using IndexedDB — no external deps, lint-clean.
import type { PostHog } from 'posthog-js';

export type QueuedEvent = {
	id?: number;
	name: string;
	properties?: Record<string, any>;
	enqueuedAt: number;
};

type Options = {
	dbName?: string;
	storeName?: string;
	maxQueue?: number;
	batchSize?: number;
	flushOnInit?: boolean;
	enableConsoleDebug?: boolean;
};

const DEFAULTS: Required<Options> = {
	dbName: 'ph_offline_db',
	storeName: 'events',
	maxQueue: 5000,
	batchSize: 200,
	flushOnInit: true,
	enableConsoleDebug: false,
};

type TxMode = 'readonly' | 'readwrite';

function promisifyReq<T = any>(req: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
}

function openDB(dbName: string, storeName: string): Promise<IDBDatabase> {
	return new Promise((resolve, reject) => {
		const req = indexedDB.open(dbName, 1);
		req.onupgradeneeded = () => {
			const db = req.result;
			if (!db.objectStoreNames.contains(storeName)) {
				const store = db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: true });
				store.createIndex('enqueuedAt', 'enqueuedAt', { unique: false });
			}
		};
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error);
	});
}

function createIdbQueue(dbName: string, storeName: string) {
	let db: IDBDatabase | null = null;

	function ensureDb(): IDBDatabase {
		if (!db) throw new Error('IDB not initialized');
		return db;
	}

	function store(mode: TxMode) {
		const sdb = ensureDb();
		const tx = sdb.transaction(storeName, mode);
		return tx.objectStore(storeName);
	}

	return {
		async init() {
			db = await openDB(dbName, storeName);
		},
		async count(): Promise<number> {
			const s = store('readonly');
			return promisifyReq<number>(s.count());
		},
		async add(ev: Omit<QueuedEvent, 'id'>): Promise<number> {
			const s = store('readwrite');
			// eslint-disable-next-line no-undef
			const key = await promisifyReq<IDBValidKey>(s.add(ev));
			return key as number;
		},
		async getOldestBatch(limit: number): Promise<QueuedEvent[]> {
			const s = store('readonly');
			const out: QueuedEvent[] = [];
			return new Promise((resolve, reject) => {
				const req = s.openCursor();
				req.onerror = () => reject(req.error);
				req.onsuccess = () => {
					const cursor = req.result as IDBCursorWithValue | null;
					if (!cursor || out.length >= limit) return resolve(out);
					out.push(cursor.value as QueuedEvent);
					cursor.continue();
				};
			});
		},
		async deleteMany(ids: number[]): Promise<void> {
			if (!ids.length) return;
			const s = store('readwrite');
			await Promise.all(ids.map((id) => promisifyReq(s.delete(id))));
		},
		async pruneToMax(max: number): Promise<void> {
			const total = await this.count();
			if (total <= max) return;
			const toDelete = total - max;
			const s = store('readwrite');
			await new Promise<void>((resolve, reject) => {
				let removed = 0;
				const req = s.openCursor();
				req.onerror = () => reject(req.error);
				req.onsuccess = () => {
					const cursor = req.result as IDBCursorWithValue | null;
					if (!cursor) return resolve();
					cursor.delete();
					removed++;
					if (removed >= toDelete) return resolve();
					cursor.continue();
				};
			});
		},
		async clear(): Promise<void> {
			const s = store('readwrite');
			await promisifyReq(s.clear());
		},
	};
}

export function setupPosthogOfflineIdb(posthog: PostHog, opts?: Options) {
	const cfg = { ...DEFAULTS, ...(opts || {}) };
	const queue = createIdbQueue(cfg.dbName, cfg.storeName);
	let ready = false;
	let flushing = false;

	const debug = (...args: any[]) => {
		if (cfg.enableConsoleDebug) console.debug('[ph-idb]', ...args);
	};

	async function ensureInit() {
		if (ready) return;
		try {
			await queue.init();
			ready = true;
		} catch (e) {
			console.warn('[ph-idb] IndexedDB unavailable; wrapper is no-op.', e);
			ready = false;
		}
	}

	async function enqueue(name: string, properties?: Record<string, any>) {
		await ensureInit();
		if (!ready) return; // degrade silently
		await queue.add({ name, properties, enqueuedAt: Date.now() });
		await queue.pruneToMax(cfg.maxQueue);
		debug('enqueued', name);
	}

	async function flushBatch(batchSize: number): Promise<boolean> {
		if (!navigator.onLine) return false;
		await ensureInit();
		if (!ready) return false;

		const batch = await queue.getOldestBatch(batchSize);
		if (!batch.length) return false;

		debug('flushing batch', batch.length);
		for (let i = 0; i < batch.length; i++) {
			const ev = batch[i];
			try {
				posthog.capture(ev.name, ev.properties);
			} catch {
				debug('capture threw, stopping');
				break;
			}
		}
		const ids = batch.map((e) => e.id!).filter(Boolean);
		await queue.deleteMany(ids);
		return true;
	}

	// top-level recursive drain (no inner declarations, no await-in-loop)
	async function drainQueue(iter = 0): Promise<void> {
		if (!navigator.onLine || iter >= 50) return;
		const sent = await flushBatch(cfg.batchSize);
		const remaining = await queue.count();
		if (sent && remaining > 0) {
			await drainQueue(iter + 1);
		}
	}

	async function flushQueue(): Promise<void> {
		if (flushing || !navigator.onLine) return;
		flushing = true;
		try {
			await drainQueue(0);
			debug('flush complete');
		} catch (e) {
			console.warn('[ph-idb] flush error', e);
		} finally {
			flushing = false;
		}
	}

	async function capture(name: string, properties?: Record<string, any>) {
		if (!navigator.onLine) {
			await enqueue(name, properties);
			return;
		}
		try {
			posthog.capture(name, properties);
		} catch {
			await enqueue(name, properties);
		}
	}

	function onOnline() {
		flushQueue().catch((e) => console.warn('[ph-idb] online flush error', e));
	}
	function onVisibility() {
		if (document.visibilityState === 'visible') {
			flushQueue().catch((e) => console.warn('[ph-idb] vis flush error', e));
		}
	}

	// non-blocking init + optional flush after init (scheduled)
	// eslint-disable-next-line @typescript-eslint/no-misused-promises
	window.addEventListener('online', onOnline);
	document.addEventListener('visibilitychange', onVisibility);

	if (cfg.flushOnInit) {
		setTimeout(() => {
			ensureInit()
				.then(() => flushQueue())
				.catch((e) => console.warn('[ph-idb] flushOnInit error', e));
		}, 0);
	}

	return {
		capture,
		flushQueue,
		teardown: () => {
			window.removeEventListener('online', onOnline);
			document.removeEventListener('visibilitychange', onVisibility);
		},
	};
}
